import axios from "axios";
import { useConfigStore } from "@/store/config";
import { showToast } from "@/utils/toast";
import { useDialogStore } from "@/store/dialog";
import { t } from "@/utils/i18n";

// ===== 動態api 路徑追蹤 =====
if (import.meta.env.VITE_BASE_API == "") {
  // 1. 取得當前網址資訊
  const host = window.location.hostname; // 例如 www-tx.lc9527.com
  const parts = host.split("."); // ["www-tx", "lc9527", "com"]

  // 2. 拆出關鍵字：如果是 www-tx 就拿 tx，如果是 localhost 就拿 localhost
  const hostKey = parts[0].includes("-") ? parts[0].split("-").pop() : parts[0];

  // 3. 準備變數 (不使用 .value，直接賦值)
  let apiUrl = "";
  let socketUrl = "";

  switch (hostKey) {
    case "localhost":
      console.log("本地開發環境");
      apiUrl = import.meta.env.VITE_BASE_API;
      socketUrl = import.meta.env.VITE_SOCKET_URL;
      break;

    case "www":
      // 處理沒有橫槓的情況，例如 www.lc9527.com
      apiUrl = `https://api.${parts[1]}.${parts[2]}/api`;
      socketUrl = `https://ws.${parts[1]}.${parts[2]}`;
      break;

    default:
      // 處理有橫槓的情況，例如 www-tx -> hostKey 就是 tx
      apiUrl = `https://api-${hostKey}.${parts[1]}.${parts[2]}/api`;
      socketUrl = `https://ws-${hostKey}.${parts[1]}.${parts[2]}`;
      break;
  }

  // 4. 重要：存入 window，讓全專案（如 axios）都能讀到
  window.CONFIG = {
    API: apiUrl,
    WS: socketUrl,
  };
  console.log("✅ API 初始化成功:", window.CONFIG);
}
const NO_RETRY_APIS = ["/table/stream"];

// 建立 Axios 實例
const service = axios.create({
  baseURL: import.meta.env.VITE_BASE_API || window.CONFIG?.API,
  timeout: 5000,
  headers: { "Content-Type": "application/json" },
});

// ===== 發送 Matomo 事件 =====
function trackApi(type, dataInfo) {
  if (window._paq) {
    window._paq.push([
      "trackEvent",
      "api",
      type,
      dataInfo, // info
      Date.now(),
    ]);
  }
}

// ===== 前置攔截器 =====
service.interceptors.request.use(
  (config) => {
    const configStore = useConfigStore();
    const raw = localStorage.getItem("config");
    const token = raw ? JSON.parse(raw).token : "";
    config.type = config.url.split("/").pop();
    config.traceId = String(configStore.generateChars(8));
    let data = config.method == "post" ? config.data : config.params;
    // console.log("api request:", config.traceId);
    let formattedData = "";
    if (data && typeof data === "object") {
      formattedData = Object.entries(data)
        .map(([key, value]) => `${key}:${value}`)
        .join(", ");
    } else if (typeof data === "string") {
      try {
        const parsed = JSON.parse(data);
        formattedData = Object.entries(parsed)
          .map(([key, value]) => `${key}:${value}`)
          .join(", ");
      } catch {
        formattedData = data;
      }
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const finalData = formattedData
      ? `trace_id:${config.traceId}, ${formattedData}`
      : `trace_id:${config.traceId}`;
    // console.log("api request:", config);
    /** matomo request */
    trackApi(`api_${config.type}_request`, finalData);
    // console.log("api request", finalData);
    return config;
  },
  (error) => {
    console.error("[Request Error]", error);
    const config = error.config;
    /** matomo request */
    trackApi(
      `api_${config.type}_request`,
      `error:${error},traceId:${config.traceId}`,
    );
    return Promise.reject(error);
  },
);

// ===== 後置攔截器 =====
service.interceptors.response.use(
  (response) => {
    const res = response.data;
    const config = response.config;
    const configStore = useConfigStore();
    const dialogStore = useDialogStore();
    // console.log("api response:", config.traceId);
    trackApi(
      `api_${config.type}_response`,
      `status:200,api_code:${res.status},access_code:${configStore.getUser},trace_id:${config.traceId}`,
    );
    if (res.status) {
      switch (res.status) {
        case "0000":
          return res;
        case "1002":
          configStore.userStatus = "LoggedIn";
          break;
        case "1003":
        case "1004":
          configStore.userStatus = "tokenExpired";
        case "1005":
          dialogStore.showDialog(t("error.tokenExpired"), "logout", "inactive"); // 叫出 dialog
          return Promise.reject(new Error(res.message || "請重新登入"));
        case "2006":
          dialogStore.showDialog(
            `${res.message_zh},code:2006 失敗`,
            "logout",
            "inactive",
          ); // 叫出 dialog
          return Promise.reject(new Error(res.message || "請重新登入"));
        case "5001":
          dialogStore.showDialog(
            `${res.message_zh},code:5001 失敗`,
            "logout",
            "inactive",
          ); // 叫出 dialog
          return Promise.reject(new Error(res.message || "維護中"));
        case "9999":
          showToast("message:" + res.message || "失敗", "danger", 5000, true);
          configStore.logout();
          return Promise.reject(new Error(res.message || "Error"));
          break;
        default:
          if (res.message == "Invalid access code") {
            configStore.userStatus = "notExist";
            return Promise.reject(new Error(res.message || "Error"));
          }
          showToast("message:" + res.message || "失敗", "danger", 3000, true);
          return Promise.reject(new Error(res.message || "Error"));
      }
    }
    // hideGlobalLoading()
  },

  async (error) => {
    const config = error.config;
    console.log("api error", error);
    // 網路連線失敗的情況
    trackApi(
      `api_${config.type}_response`,
      `status:${error.status},api_msg:${error.message},,trace_id:${config.traceId}`,
    );
    if (!config) return Promise.reject(error);
    // console.log(config.url);
    // ===== retry 設定  =====
    const MAX_ATTEMPTS = 3;
    config.__attempt = config.__attempt || 1;
    const isNetworkError = !error.response;
    const isTimeout = error.code === "ECONNABORTED";
    const is5xx = error.response?.status >= 500;
    const isStream = config.url != "/table/stream";
    const shouldRetry = isNetworkError || isTimeout || is5xx || isStream;
    if (shouldRetry && config.__attempt < MAX_ATTEMPTS) {
      config.__attempt += 1;
      console.error(
        `API retry ${config.__attempt}/${MAX_ATTEMPTS}`,
        config.url,
      );
      // 設定1秒
      await new Promise((r) => setTimeout(r, 1000));
      return service(config);
    }

    if (error.response) {
      if (error.response) {
        const statusCode = error.response.status;
        switch (statusCode) {
          case 403:
            console.error("沒有權限訪問此資源");
            break;
          case 404:
            console.error("找不到資源");
            break;
          case 500:
            console.error("伺服器內部錯誤");
            break;
          default:
            console.error(error.message);
        }
      } else {
        // DNS / Network error（含 ERR_NAME_NOT_RESOLVED）
      }
    }
    // showToast(`message:${error.message}`, "danger", 4000, true);
    return Promise.reject(error);
  },
);

// ===== 封裝統一的 HTTP 方法 =====
const request = {
  get(url, params = {}, config = {}) {
    return service.get(url, { params, ...config });
  },
  post(url, data = {}, config = {}) {
    return service.post(url, data, config);
  },
  put(url, data = {}, config = {}) {
    return service.put(url, data, config);
  },
  delete(url, data = {}, config = {}) {
    // DELETE 若要帶 body 要這樣寫
    return service.delete(url, { data, ...config });
  },
};

export default request;
